import React from 'react';
import PropTypes from 'prop-types';
import { useRouter } from 'next/router';
import { Modal, Tooltip } from 'antd';
import Link from '../../Link';
import { LocalizationContext } from '../../../hoc/Localization';
import { SettingsContext } from '../../../hoc/Settings';
import { UserContext } from '../../../hoc/User';
import { SecurityContext } from '../../../hoc/Security';
import SchoolLink from '../../TinyComponents/SchoolLink';
import IconText from '../../TinyComponents/IconText';
import Rating from '../../Rating';
import usePriceFormat from '../../../utils/priceFormat';
import ScheduleMoment from '../ScheduleMoment';
import ShareAndFavourite from '../../TinyComponents/ShareAndFavourite';
import RegisterButton from '../RegisterButton';
import Badges from '../Badges';
import Price from '../Price';
import styles from '../Header/styles.less';

const Header = ({
    type,
    course,
    schedules,
    setRegister,
    selectedSchedule,
    setSelectedSchedule,
    showMonthlyPrice,
    mediumUrl,
    canRegister,
    isRegistered,
    join,
    lastRequestId,
    isFavourite,
    handleFavouriteClick,
    favourites,
    canEdit,
}) => {
    const { t, locale, translate } = React.useContext(LocalizationContext);
    const {
        domainSettings: { domain },
    } = React.useContext(SettingsContext);
    const { user } = React.useContext(UserContext);
    const { favourite, setFavourite } = React.useContext(SecurityContext);

    const [cancellationModal, setCancellationModal] = React.useState(false);
    const [smallScreen, setSmallScreen] = React.useState(false);
    React.useEffect(() => {
        const windowWidth = window.screen.width;
        if (windowWidth < 768) {
            setSmallScreen(true);
        }
    }, []);

    const router = useRouter();
    const { reference, payment } = router.query;

    const {
        title,
        image,
        Group,
        level,
        price,
        rating,
        school,
        period,
        period_value: periodValue,
        language,
        location,
        currency,
        featured,
        online,
        discount,
        category,
        coursetypes,
        trainingType,
        instructions,
        free_trial: freeTrial,
        free_course: freeCourse,
        discounted_price: discountedPrice,
        monthly_price: monthlyPrice,
        'Max Group Size': maxGroupSize,
        'Min Group Size': minGroupSize,
        last_minute_start: lastMinuteStart,
        last_minute_end: lastMinuteEnd,
        last_minute_discount: lastMinuteDiscount,
        early_bird_start: earlyBirdStart,
        early_bird_end: earlyBirdEnd,
        early_bird_discount: earlyBirdDiscount,
        discount_start: discountStart,
        discount_end: discountEnd,
        self_learning: selfLearning,
    } = course;
    const symbolCurrency = {
        AMD: '֏',
        USD: '$',
        EUR: '€',
        RUB: '₽',
    };

    let currentPeriod = '';
    if (period && period !== 'custom') {
        currentPeriod = t(`course.details.content.${period}`);
    }
    if (period && period === 'custom') {
        currentPeriod = periodValue;
    }
    let courseLanguage = null;

    React.useEffect(() => {
        if (favourite && user) {
            setFavourite(false);
            // eslint-disable-next-line no-use-before-define
            handleFavouriteClick();
        }
    }, [favourite, user]);

    if (coursetypes) {
        courseLanguage = translate(coursetypes, 'title');
    }

    if (courseLanguage && language && Object.keys(language).length) {
        courseLanguage += ` (${translate(language, 'Name')})`;
    } else if (language && Object.keys(language).length) {
        courseLanguage = translate(language, 'Name');
    }

    const address =
        location && Object.keys(location).length ? [location.city, location.street] : [];
    if (location && location.house_number) {
        address.push(location.house_number);
    }
    address.push(location.zip_code);

    let groupSize = '';

    if (Group && maxGroupSize) {
        groupSize = `${maxGroupSize}`;

        if (minGroupSize) {
            groupSize = `${minGroupSize} - ${maxGroupSize}`;
        }
    }

    let showGroup;

    if (Group) {
        showGroup = `${t('details.edit.courses.form.group_yes')} ${groupSize}`;
    } else {
        showGroup = t('details.edit.courses.form.group_no');
    }

    let favouriteUrl = '/static/images/course/heartBlack.svg';
    if (isFavourite) {
        favouriteUrl = '/static/images/course/heartChecked.svg';
    }

    return (
        <div id="courseHeader" className={`${styles.container} ${styles[type]}`}>
            {/* ----------Image---------- */}
            <div className={styles.imageContainer}>
                <img src={!image.includes('static') ? mediumUrl : image} alt={title} />
            </div>
            {/* ----------Info---------- */}
            <div className={styles.infoContainer}>
                {/* ----------Title and Rating---------- */}
                <div className={styles.header}>
                    {/* ----------Title---------- */}
                    <div className={styles.shareInfo}>
                        <Badges online={online} featured={featured} freeTrial={freeTrial} />
                        <ShareAndFavourite
                            course={course}
                            domain={domain}
                            favouriteUrl={favouriteUrl}
                            handleFavouriteClick={handleFavouriteClick}
                            locale={locale}
                            school={school}
                        />
                    </div>
                    <div className={styles.title}>
                        <div style={{ width: '65%' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                <h1
                                    style={{ overflow: 'hidden' }}
                                    className={styles.titleNameCourse}
                                >
                                    <span>{course.title || course[`title_${locale}`]}</span>
                                </h1>
                                {smallScreen && <ScheduleMoment style={styles} data={course} />}
                            </div>
                            {!!school && !!Object.keys(school).length && (
                                <div className={styles.schoolLinkCoursePage}>
                                    <SchoolLink school={school} />
                                </div>
                            )}
                        </div>
                        {!smallScreen && <ScheduleMoment style={styles} data={course} />}
                    </div>
                </div>

                {/* ----------Description---------- */}
                <div className={styles.descriptionRow}>
                    <div className={styles.descriptionCol}>
                        {!!trainingType && (
                            <div className={styles.trainingType}>
                                <IconText
                                    icon="/static/images/course/webinar.svg"
                                    text={`${translate(trainingType, 'title')}`}
                                />
                            </div>
                        )}
                        {!!address && !!address.length && (
                            <div className={styles.location}>
                                <a href="#map">
                                    <IconText
                                        icon="/static/images/course/location.svg"
                                        text={<span>{address.join(' ')}</span>}
                                    />
                                </a>
                            </div>
                        )}

                        {!!level && (
                            <div className={styles.groupingCourse}>
                                <IconText
                                    icon="/static/images/course/level.svg"
                                    text={`${translate(level, 'Name')}`}
                                />
                                <span className={styles.groupCount}>
                                    {Group ? (
                                        <IconText
                                            icon="/static/images/course/group.svg"
                                            text={`${showGroup}`}
                                        />
                                    ) : null}
                                </span>
                            </div>
                        )}
                        {!!courseLanguage && (
                            <div className={styles.categoryTypes}>
                                {course &&
                                course.coursetypes &&
                                course.coursetypes.showLandingPage ? (
                                    <Link
                                        route="subLanding"
                                        category={category.label}
                                        type={courseLanguage}
                                    >
                                        <div className={styles.courseLang}>
                                            <IconText
                                                icon="/static/images/course/courseLng.svg"
                                                text={`${courseLanguage}`}
                                            />
                                        </div>
                                    </Link>
                                ) : (
                                    <div className={styles.courseLang}>
                                        <IconText
                                            icon="/static/images/course/courseLng.svg"
                                            text={`${courseLanguage}`}
                                        />
                                    </div>
                                )}
                                {/* ----------Category---------- */}
                                <Link route="landing" category={category.label}>
                                    <div className={styles.categoryContainer}>
                                        <span>
                                            {!!category && (
                                                <div className={styles.category}>
                                                    {translate(category, 'name')}
                                                </div>
                                            )}
                                        </span>
                                    </div>
                                </Link>
                            </div>
                        )}
                        {!!selfLearning && (
                            <IconText
                                icon="/static/images/course/selfLearning.png"
                                text={t('course.edit.selfLearning')}
                            />
                        )}
                    </div>
                </div>
                {/* ----------Price and Register---------- */}
                <div className={styles.footerCardInfo}>
                    <div className={styles.flexibleContainer}>
                        <Rating
                            rating={rating}
                            favourites={favourites || '0'}
                            visits={canEdit && course.visits ? course.visits : 0}
                        />
                    </div>
                    <div>
                        <div className={styles.priceCol} style={{ marginBottom: '5px' }}>
                            <Price course={course} showMonthlyPrice={showMonthlyPrice} />
                        </div>
                        {type === 'header' && (
                            <div className={styles.registerWrapper}>
                                {/* ----------Register---------- */}
                                {instructions && isRegistered && (
                                    <Tooltip color="white" title={instructions}>
                                        <img
                                            className={styles.registerIcon}
                                            src="/static/images/course/info.svg"
                                            alt="info"
                                        />
                                    </Tooltip>
                                )}
                                <RegisterButton
                                    canRegister={canRegister}
                                    selectedSchedule={selectedSchedule}
                                    setSelectedSchedule={setSelectedSchedule}
                                    course={course}
                                    schedules={schedules}
                                    setRegister={setRegister}
                                    join={join}
                                    lastRequestId={lastRequestId}
                                    reference={reference}
                                    payment={payment}
                                />
                                <Modal
                                    visible={cancellationModal}
                                    title="Cancellation"
                                    onCancel={() => {
                                        setCancellationModal(false);
                                    }}
                                    onOk={() => {
                                        setCancellationModal(false);
                                    }}
                                >
                                    {course.cancellation}
                                </Modal>
                            </div>
                        )}
                    </div>
                </div>
                {!!course.cancellation && (
                    <span
                        onClick={() => {
                            Modal.info({
                                title: t('course.details.content.cancellation'),
                                content: <p>{course.cancellation}</p>,
                            });
                        }}
                        className={styles.cancellation}
                    >
                        {t('course.details.content.cancellation')}
                    </span>
                )}
            </div>
        </div>
    );
};

Header.propTypes = {
    type: PropTypes.string,
    course: PropTypes.object,
    schedules: PropTypes.array,
    setRegister: PropTypes.func,
    showMonthlyPrice: PropTypes.bool,
    selectedSchedule: PropTypes.object,
    setSelectedSchedule: PropTypes.func,
    mediumUrl: PropTypes.string.isRequired,
    canRegister: PropTypes.bool.isRequired,
    isRegistered: PropTypes.bool,
    join: PropTypes.bool.isRequired,
    lastRequestId: PropTypes.string,
    isFavourite: PropTypes.bool,
    favourites: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    handleFavouriteClick: PropTypes.func.isRequired,
    canEdit: PropTypes.bool,
};

Header.defaultProps = {
    type: 'header',
    course: {},
    schedules: [],
    setRegister: () => {},
    selectedSchedule: null,
    showMonthlyPrice: false,
    setSelectedSchedule: () => {},
    lastRequestId: '',
    isFavourite: false,
    favourites: 0,
    canEdit: false,
    isRegistered: false,
};

export default React.memo(Header);
